import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from '@/lib/cn'
import { toInitials } from '@/lib/format'

const SIZES = {
  xs: 'size-6 text-[10px]',
  sm: 'size-7 text-[11px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
} as const

export type AvatarSize = keyof typeof SIZES

export interface AvatarProps {
  name: string
  src?: string | null
  initials?: string
  size?: AvatarSize
  className?: string
}

/**
 * Avatar med initialer som fallback. Billedet er dekorativt (`alt=""`), fordi
 * navnet altid findes i den omgivende kontekst eller i `title`.
 */
export function Avatar({ name, src, initials, size = 'md', className }: AvatarProps) {
  const fallback = initials ?? toInitials(name)

  return (
    <AvatarPrimitive.Root
      title={name}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'border border-line-strong bg-gradient-to-br from-brand-500/45 to-info-500/35',
        'font-semibold text-text-primary',
        SIZES[size],
        className,
      )}
    >
      {src && (
        <AvatarPrimitive.Image src={src} alt="" className="size-full object-cover" />
      )}
      <AvatarPrimitive.Fallback delayMs={src ? 300 : 0} className="leading-none">
        {fallback}
      </AvatarPrimitive.Fallback>
      <span className="sr-only">{name}</span>
    </AvatarPrimitive.Root>
  )
}

export interface AvatarGroupProps {
  people: Array<{ userId: string; fullName: string; initials?: string; avatarUrl?: string | null }>
  /** Antal avatarer der vises, før resten samles i en tæller. */
  max?: number
  /** Ekstra deltagere ud over `people`, fx fra en server-side optælling. */
  additionalCount?: number
  size?: AvatarSize
  className?: string
}

export function AvatarGroup({
  people,
  max = 3,
  additionalCount = 0,
  size = 'md',
  className,
}: AvatarGroupProps) {
  const visible = people.slice(0, max)
  const overflow = Math.max(people.length - max, 0) + additionalCount

  return (
    <div
      className={cn('flex items-center', className)}
      role="group"
      aria-label={`${people.length + additionalCount} deltagere`}
    >
      {visible.map((person, index) => (
        <div
          key={person.userId}
          // Overlap, hvor den første avatar ligger øverst.
          className="-ml-2 first:ml-0"
          style={{ zIndex: visible.length - index }}
        >
          <Avatar
            name={person.fullName}
            initials={person.initials}
            src={person.avatarUrl}
            size={size}
            className="ring-2 ring-app-950"
          />
        </div>
      ))}

      {overflow > 0 && (
        <span
          className={cn(
            '-ml-2 inline-flex items-center justify-center rounded-full',
            'border border-line-strong bg-app-800 font-semibold text-text-secondary ring-2 ring-app-950',
            SIZES[size],
          )}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
